/**
 * Structured logger. Prefer this over console.* across the codebase.
 * Output is JSON-friendly so it can be parsed by log aggregators.
 *
 * All log payloads pass through `redactLogMetadata` so PII / secrets
 * (emails, JWTs, bearer tokens, Stripe keys/ids, IPs, phone numbers,
 * Telegram bot tokens, webhook signatures, cookies, session ids,
 * API keys) never reach stdout. Audit-log rows in the DB are NOT routed
 * through this — they use `sanitizeAuditData` from `./security/pii`.
 *
 * In production we suppress debug/info noise; warn/error always emit.
 */

import { redactLogMetadata } from "./security/redact";

type LogLevel = "debug" | "info" | "warn" | "error";

const isProd =
  (typeof process !== "undefined" && process.env?.NODE_ENV === "production") ||
  (typeof import.meta !== "undefined" && (import.meta as { env?: { PROD?: boolean } }).env?.PROD === true);

function serializeLogValue(value: unknown): unknown {
  if (value instanceof Error) {
    return {
      name: value.name,
      message: value.message,
      stack: value.stack,
      cause: serializeLogValue(value.cause),
    };
  }

  if (value && typeof value === "object") {
    try {
      return JSON.parse(JSON.stringify(value));
    } catch {
      return Object.prototype.toString.call(value);
    }
  }

  return value;
}

function emit(level: LogLevel, args: unknown[]) {
  const serialized = args.length === 1 ? serializeLogValue(args[0]) : args.map(serializeLogValue);
  const payload = {
    ts: new Date().toISOString(),
    level,
    msg: redactLogMetadata(serialized),
  };
  // eslint-disable-next-line no-console
  const sink = level === "error" ? console.error : level === "warn" ? console.warn : console.log;
  try {
    sink(JSON.stringify(payload));
  } catch {
    // eslint-disable-next-line no-console
    sink(payload);
  }
}

export const logger = {
  debug: (...args: unknown[]) => {
    if (isProd) return;
    emit("debug", args);
  },
  info: (...args: unknown[]) => {
    if (isProd) return;
    emit("info", args);
  },
  warn: (...args: unknown[]) => emit("warn", args),
  error: (...args: unknown[]) => emit("error", args),
};

export { redactPII, redactLogMetadata, hashIdentifier } from "./security/redact";

export default logger;

